"use client";
import type React from "react";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { env } from "@/env";
import { useParams } from "next/navigation";
import { createContext, useContext, useEffect, useState } from "react";

type FeatheryState =
  | { error: unknown; sdk?: undefined; status: "error" }
  | { error?: undefined; sdk: FeatherySDK; status: "ready" }
  | { error?: undefined; sdk?: undefined; status: "idle" | "loading" };

const FeatheryCtx = createContext<FeatheryState>({ status: "idle" });

export function FeatheryProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<FeatheryState>({ status: "idle" });
  const params = useParams();

  useEffect(() => {
    if (!params.locale) return;
    setState({ status: "loading" });
    loadFeatherySDK({ apiKey: env.NEXT_PUBLIC_FEATHERY_SDK_KEY, language: params.locale as string })
      .then(sdk => setState({ status: "ready", sdk }))
      .catch(error => {
        setState({ status: "error", error });
        captureEvent(EventName.mw_contact_sales_feathery_load_failed, {
          error_message: error instanceof Error ? error.message : String(error),
          locale: params.locale,
        });
      });
  }, [params.locale]);

  return <FeatheryCtx.Provider value={state}>{children}</FeatheryCtx.Provider>;
}

export function useFeathery() {
  const ctx = useContext(FeatheryCtx);
  if (!ctx) throw new Error("useFeathery must be used within FeatheryProvider");
  return ctx;
}

let sdkPromise: Promise<FeatherySDK> | null = null;

export type FeatherySDK = {
  init: (apiKey: string, options?: { language?: string }) => void;
  renderAt: (
    containerId: string,
    options: {
      formId: string;
      onAction?: (context: FormContext) => void;
      onChange?: (context: FormContext) => void;
      onError?: (context: ErrorContext) => void;
      onFormComplete?: (context: FormContext) => void;
      onLoad?: () => void;
      onSubmit?: (context: FormContext) => void;
      onView?: (context: FormContext) => void;
    },
    embedded?: boolean,
  ) => FormContext;
  setFieldValues: (values: { [key: string]: unknown }) => void;
};

export type FormContext = {
  destroy: () => void;
  setFieldValues: (values: unknown) => void;
  submitFields: { [key: string]: { value: string } };
} | null;

export type ErrorContext = FormContext & {
  errorFieldId?: string;
  errorFieldType?: string;
  errorMessage?: string;
  trigger?: { type: string; text: string };
};

function injectScript(src: string) {
  return new Promise<void>((resolve, reject) => {
    const el = document.createElement("script");
    el.src = src;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = reject;
    document.head.appendChild(el);
  });
}

export function loadFeatherySDK(opts: { apiKey: string; language: string; src?: string }) {
  if (typeof window === "undefined") {
    // SSR guard: don't touch window/document
    return Promise.reject(new Error("Feathery SDK cannot load on server"));
  }

  if (!sdkPromise) {
    sdkPromise = (async () => {
      const src = opts.src || "https://cdn.jsdelivr.net/npm/@feathery/react@2.4.74/umd/index.js"; // Pin to current latest version which is working. As of 2025-08-21
      await injectScript(src);

      const sdk = (window as typeof window & { Feathery: FeatherySDK }).Feathery;

      if (!sdk) {
        throw new Error("Feathery SDK failed to load");
      }

      sdk.init(opts.apiKey, { language: opts.language });

      return sdk;
    })();
  }
  return sdkPromise;
}
